/**
 * @license
 *
 * Copyright IBM Corp. 2019, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import CDSDropdown, {
  DROPDOWN_TYPE,
} from '@carbon/web-components/es/components/dropdown/dropdown.js';

import styles from './search-with-typeahead.scss';
import { DROPDOWN_COLOR_SCHEME, DROPDOWN_SIZE } from '../footer/defs';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

export { DROPDOWN_COLOR_SCHEME, DROPDOWN_SIZE, DROPDOWN_TYPE };

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Dropdown component to change search scope.
 *
 * @element c4d-scoped-search-dropdown
 * @fires cds-select-selected - The custom event fired after the the dropdown value is changed upon a user gesture.
 */
@customElement(`${c4dPrefix}-scoped-search-dropdown`)
class C4DScopedSearchDropdown extends CDSDropdown {
  /**
   * Handles user-initiated selection of a dropdown item
   *
   * @param [item] The dropdown item user wants to select. Absense of this argument means clearing selection.
   */
  protected _handleUserInitiatedSelectItem(item?: any) {
    if (this._selectionShouldChange(item)) {
      const init = {
        bubbles: true,
        composed: true,
        detail: {
          appId: item.value,
        },
      };
      const constructor = this.constructor as typeof C4DScopedSearchDropdown;
      const beforeSelectEvent = new CustomEvent(constructor.eventBeforeSelect, {
        ...init,
        cancelable: true,
      });
      if (this.dispatchEvent(beforeSelectEvent)) {
        this._selectionDidChange(item);
        const afterSelectEvent = new CustomEvent(constructor.eventSelect, init);
        this.dispatchEvent(afterSelectEvent);
      }
    }
  }

  /**
   * The name of the custom event captured to retrieve the new search scope.
   */
  static get eventSelect() {
    return `${prefix}-select-selected`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DScopedSearchDropdown;
